// Shared helpers for the ops demo data layer: language-aware names, time
// helpers, deterministic picks and inline SVG "photos". No app imports beyond
// src/types so the demo API can never pull in services or stores.

import { hashString, iso } from '../core';

/** The ops app keeps its UI language in localStorage under this key (see i18n/app.ts). */
const LANGUAGE_KEY = 'ops_app_language';

export function demoLanguage(): 'ar' | 'en' {
  try {
    const value = globalThis.localStorage?.getItem(LANGUAGE_KEY);
    return value && value.startsWith('ar') ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}

/** Arabic when the UI is in Arabic and an Arabic name exists; English otherwise. */
export function tr(en: string, ar?: string | null): string {
  return demoLanguage() === 'ar' && ar ? ar : en;
}

export function nowIso(): string {
  return iso(new Date());
}

/** Deterministic 0..1 value for a key (stable across reloads and days). */
export function unit(key: string): number {
  return (hashString(key) % 100000) / 100000;
}

export function pickBy<T>(key: string, items: readonly T[]): T {
  return items[hashString(key) % items.length];
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function mapsUrl(lat: number, lng: number): string {
  return `https://maps.google.com/?q=${lat},${lng}`;
}

export function toNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** Body of a request as a plain object (FormData is flattened, JSON strings parsed). */
export function bodyOf(body: unknown): Record<string, unknown> {
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const out: Record<string, unknown> = {};
    body.forEach((value, key) => {
      out[key] = typeof value === 'string' ? value : (value as File).name;
    });
    return out;
  }
  if (typeof body === 'string') {
    try {
      return asRecord(JSON.parse(body));
    } catch {
      return {};
    }
  }
  return asRecord(body);
}

export function includesText(haystack: Array<string | null | undefined>, needle: string | undefined): boolean {
  if (!needle) return true;
  const q = needle.trim().toLowerCase();
  if (!q) return true;
  return haystack.some((h) => (h ?? '').toLowerCase().includes(q));
}

/** Incrementing counter for ids of records created during the session. */
export function makeCounter(start: number): { next: () => number; reset: () => void } {
  let n = start;
  return {
    next: () => {
      n += 1;
      return n;
    },
    reset: () => {
      n = start;
    },
  };
}

function escapeXml(s: string): string {
  return s.replace(/[<>&'"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' })[c] as string);
}

/**
 * A small self-contained SVG "photo" (data URI) so evidence galleries render
 * without any network request.
 */
export function placeholderPhoto(title: string, subtitle: string, hue: number): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="480" viewBox="0 0 480 480">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="hsl(${hue},45%,62%)"/><stop offset="1" stop-color="hsl(${(hue + 40) % 360},40%,38%)"/>` +
    `</linearGradient></defs>` +
    `<rect width="480" height="480" fill="url(#g)"/>` +
    `<rect x="120" y="150" width="240" height="150" rx="14" fill="none" stroke="#fff" stroke-opacity=".75" stroke-width="10"/>` +
    `<circle cx="240" cy="225" r="42" fill="none" stroke="#fff" stroke-opacity=".75" stroke-width="10"/>` +
    `<rect x="200" y="128" width="80" height="28" rx="6" fill="#fff" fill-opacity=".75"/>` +
    `<text x="240" y="360" font-family="Arial, sans-serif" font-size="26" font-weight="700" fill="#fff" text-anchor="middle">${escapeXml(title)}</text>` +
    `<text x="240" y="396" font-family="Arial, sans-serif" font-size="18" fill="#fff" fill-opacity=".85" text-anchor="middle">${escapeXml(subtitle)}</text>` +
    `<text x="240" y="452" font-family="Arial, sans-serif" font-size="14" fill="#fff" fill-opacity=".7" text-anchor="middle">DEMO</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** A hand-drawn-looking signature as an SVG data URI. */
export function signatureImage(name: string): string {
  const seed = hashString(name);
  const wobble = (i: number) => 40 + ((seed >> (i % 16)) % 30);
  const path = `M20 ${wobble(1)} C 60 ${wobble(2) - 30}, 90 ${wobble(3) + 25}, 130 ${wobble(4)} S 200 ${wobble(5) - 20}, 240 ${wobble(6)} S 300 ${wobble(7) + 20}, 340 ${wobble(8) - 10}`;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="360" height="110" viewBox="0 0 360 110">` +
    `<rect width="360" height="110" fill="#fff"/>` +
    `<path d="${path}" fill="none" stroke="#1f2a5a" stroke-width="3" stroke-linecap="round"/>` +
    `<text x="20" y="100" font-family="Arial, sans-serif" font-size="12" fill="#6b7280">${escapeXml(name)}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
